import posthog from 'posthog-js'
import { posthog as posthogConfig, azureAD } from '@/const.js'

// App-level super properties: constant for the lifetime of the app, unrelated
// to the logged-in user. reset() (called on logout) clears these too, so they
// must be re-registered right after every reset().
export function registerAppContext() {
  if (!posthogConfig.key) return

  posthog.register({
    app_name: posthogConfig.appName,
    platform: posthogConfig.platform,
    environment: posthogConfig.environment
  })
}

export function initPostHog() {
  if (!posthogConfig.key) return

  posthog.init(posthogConfig.key, {
    api_host: posthogConfig.host,
    person_profiles: 'identified_only',
    autocapture: false // stop tracking clicks/form submits; keep pageviews only
  })
  registerAppContext()
}

function getLoggedInUserId(store) {
  const user = store.state.user.loggedInUser
  return (user && user.id) || null
}

// identify(userId + '-' + policy) then setPersonProperties({ user_role, name, email }),
// per spec - kept in sync whenever the logged-in user changes. DisplayName/Email
// can be empty for some users, so name/email may come through blank.
export function identifyUser(store) {
  if (!posthogConfig.key) return

  const userId = getLoggedInUserId(store)
  if (!userId) return

  const policy = (
    localStorage.getItem('currentPolicy') || azureAD.policy
  ).toLowerCase()
  posthog.identify(`${userId}-${policy}`)
  posthog.setPersonProperties({
    user_role: store.getters['user/selecteOrganisationRoleName'],
    name: store.getters['user/displayName'],
    email: store.getters['user/email']
  })
}

// Organisation/facility/floor/area working context, per the spec: registered
// (not person properties) so it's automatically included on all future events.
// Re-registering replaces the previous values.
export function registerContext(store) {
  if (!posthogConfig.key) return

  const calculatedFilter = store.getters['filter/calculatedFilter']
  const filterNames = store.getters['filter/filterNames']

  posthog.register({
    organisation_id: store.getters['user/selecteOrganisationId'],
    organisation_name: store.getters['user/selecteOrganisationName'],
    filter_flag: calculatedFilter.flag,
    filter_list: calculatedFilter.filterIds,
    facility_name: filterNames.facilityName,
    floor_name: filterNames.floorName,
    area_name: filterNames.areaName
  })
}

// Keeps identify/register in sync with the Vuex user/filter modules for the
// lifetime of the session (e.g. when the user switches org or filter).
export function syncPostHogWithStore(store) {
  if (!posthogConfig.key) return

  identifyUser(store)
  registerContext(store)

  store.watch(() => getLoggedInUserId(store), () => identifyUser(store))
  store.watch(
    () => store.getters['user/selecteOrganisationId'],
    () => {
      // user_role is derived from the active organisation's roles, so it
      // needs to be re-identified whenever the user switches organisation.
      identifyUser(store)
      registerContext(store)
    }
  )
  store.watch(
    state => [
      state.filter.dashboardFilter,
      state.filter.buildings,
      state.filter.areaFilters
    ],
    () => registerContext(store),
    { deep: true }
  )
}

// login_type: 'interactive' (user completed the sign-in UI) or 'silent'
// (session restored from an existing refresh token / already active session).
export function captureLogin(loginType) {
  if (!posthogConfig.key) return
  posthog.capture('user_logged_in', { login_type: loginType })
}

export function captureLogout() {
  if (!posthogConfig.key) return
  posthog.capture('user_logged_out')
  posthog.reset()
  // reset() clears app_name/platform/environment too - restore them.
  registerAppContext()
}

export function captureDashboardOpened() {
  if (!posthogConfig.key) return
  posthog.capture('careplus_dashboard_opened')
}

export function capturePageview(url) {
  if (!posthogConfig.key) return
  posthog.capture('$pageview', { $current_url: url })
}

export default posthog
